import jsCookie from 'js-cookie';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GroupBase } from 'react-select';
import { capitalize } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetAllCrops } from '@/modules/settings/shared/apiHooks';
import { ISettings } from '@/modules/settings/shared/types';
import {
  InputField,
  NextImage,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import {
  areaUnits,
  farmFields,
  farmFieldsDescriptions,
} from '@/shared/constant/general';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { IGeneral } from '@/shared/types';

function StepFour() {
  const { t } = useTranslation();
  const {
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormContext();
  const [fieldName, fieldType, area, areaUnit, crop] = watch([
    'fieldName',
    'fieldType',
    'area',
    'areaUnit',
    'crop',
  ]);
  const [selectedAsset] = useStore((store) => store?.asset);
  const userObj = JSON.parse(jsCookie.get('userObj') ?? '{}');

  const { data: allCrops, isLoading } = useGetAllCrops(
    userObj?.value?.entityId ?? selectedAsset?.id
  );

  return (
    <div className='overflow-auto px-24 pt-0 pb-24'>
      <div className='mb-2 mt-7 flex flex-col gap-16'>
        <InputField
          borderColor='border-outline-variant'
          width='w-full'
          py='!py-12'
          label={t('Field Name')}
          placeholder={t('Field Name')}
          error={!!errors.fieldName}
          errorMessage={errors.fieldName?.message}
          className='!rounded-xl bg-background-transparent'
          value={fieldName}
          {...register('fieldName', {
            setValueAs: (value) => (value?.length ? value : undefined),
          })}
          required
        />
        <SelectField<IGeneral.LabelValuePair, false>
          id='fieldType'
          getLabel={(obj: IGeneral.LabelValuePair) => obj.label}
          getValue={(obj: IGeneral.LabelValuePair) => obj.value}
          isDisabled={isLoading}
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          label={t('Field Type')}
          placeholder={t('Field Type')}
          list={farmFields?.map((item) => ({
            label: t(item.toUpperCase()),
            value: item,
          }))}
          value={fieldType}
          onChange={(e) => setValue('fieldType', e)}
          py='10px'
          px='0.5rem'
          borderRadius='1rem'
          width='w-full'
          customOption={(option) => (
            <div>
              <Typography weight='bold'>{t(option.data.label)}</Typography>
              <Typography variant='subtitle2'>
                {t(
                  farmFieldsDescriptions[
                    option.data.value as ISettings.FarmFieldType
                  ]
                )}
              </Typography>
            </div>
          )}
        />
        <InputField
          borderColor='border-outline-variant'
          width='w-full'
          py='!py-12'
          label={t('Area')}
          placeholder={t('Area')}
          error={!!errors.area}
          errorMessage={errors.area?.message}
          className='!rounded-xl bg-background-transparent'
          value={area}
          {...register('area', {
            setValueAs: (value) => (value?.length ? value : undefined),
          })}
          required
          suffix={
            <SelectField
              id='areaUnit'
              placeholder={t('Unit')}
              getLabel={(obj: { label: string; value: string }) => obj.label}
              getValue={(obj: { label: string; value: string }) => obj.value}
              className='cursor-pointer !rounded-xl bg-background-transparent'
              borderColor='transparent'
              isMulti={false}
              list={areaUnits.map((unit) => ({
                label: t(unit),
                value: unit,
              }))}
              py='0'
              px='0'
              borderRadius='1rem'
              width='w-full'
              value={
                [areaUnit].map((unit) => ({
                  label: capitalize(unit.label),
                  value: capitalize(unit.value),
                }))[0]
              }
              onChange={(e) => setValue('areaUnit', e)}
            />
          }
        />
        <SelectField<
          IGeneral.ICropSchema,
          true,
          GroupBase<IGeneral.ICropSchema>
        >
          isGroup
          id='crop'
          getLabel={(obj: IGeneral.ICropSchema) => obj.name}
          getValue={(obj: IGeneral.ICropSchema) => obj.id}
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          label={t('Crop')}
          placeholder={t('Grapes')}
          isDisabled={isLoading}
          list={allCrops?.list}
          py='10px'
          px='0.5rem'
          maxMenuHeight={200}
          borderRadius='1rem'
          width='w-full'
          formatOptionLabel={(option: IGeneral.ICropSchema) => (
            <div className='flex items-center gap-12'>
              {option.image && (
                <NextImage
                  src={option.image}
                  width={24}
                  height={16}
                  alt='country flag'
                />
              )}
              {option.name}
            </div>
          )}
          value={crop}
          onChange={(e) => setValue('crop', e)}
          required
        />
      </div>
    </div>
  );
}

export { StepFour };
